<script setup lang="ts">
import { ArrowPathIcon, PlusIcon } from '@heroicons/vue/20/solid'

defineProps<{
  isOpen: boolean
}>()

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'select-import'): void
  (e: 'select-manual'): void
}>()

const handleImportClick = () => {
  navigateTo('/settings/import-data')
  emit('close')
}

const handleManualClick = () => {
  emit('select-manual')
  emit('close')
}
</script>

<template>
  <ModalContainer
    :is-open="isOpen"
    :is-overflow-hidden="false"
    :can-close-by-click-outside="true"
    class="sm:!max-w-4xl"
    @close="emit('close')"
  >
    <div class="p-6">
      <!-- Header -->
      <div class="text-center mb-8">
        <h2 class="text-2xl font-bold text-gray-900 mb-2">
          Ajouter un compte
        </h2>
        <p class="text-gray-600">
          Choisissez comment vous souhaitez ajouter votre compte.
        </p>
      </div>

      <!-- Two Cards Layout -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
        
        <!-- Manual Import Card -->
        <div 
          @click="handleManualClick"
          class="border border-gray-200 rounded-lg p-6 cursor-pointer hover:border-gray-300 hover:shadow-md transition-all duration-200 flex flex-col items-center text-center"
        >
          <div class="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
            <PlusIcon class="w-8 h-8 text-blue-600" />
          </div>
          
          <h3 class="text-lg font-semibold text-gray-900 mb-3">
            Ajout manuel
          </h3>
          
          <p class="text-gray-600 text-sm leading-relaxed text-balance">
            Vous pouvez saisir manuellement vos positions actuelles. Puis ajoutez optionnellement vos transactions (achat/vente) pour avoir vos performances annualisées et vous comparer au S&P500.
          </p>
        </div>

        <!-- Brokerage Import Card -->
        <div 
          @click="handleImportClick"
          class="border border-gray-200 rounded-lg p-6 cursor-pointer hover:border-gray-300 hover:shadow-md transition-all duration-200 flex flex-col items-center text-center"
        >
          <div class="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
            <ArrowPathIcon class="w-8 h-8 text-green-600" />
          </div>
          
          <h3 class="text-lg font-semibold text-gray-900 mb-3">
            Importation d'un courtier
          </h3>
          
          <!-- Broker Logos with Labels -->
          <div class="flex items-center justify-center gap-4 mb-4">
            <div class="flex items-center gap-2">
              <img 
                src="~/assets/svg/interactive-brokers-logo-black.svg" 
                alt="Interactive Brokers" 
                class="h-5"
              />
              <span class="text-sm text-gray-600 font-medium">Interactive Brokers</span>
            </div>
            <div class="text-gray-400">•</div>
            <div class="flex items-center gap-2">
              <img 
                src="~/assets/svg/trade-republic-logo-black.svg" 
                alt="Trade Republic" 
                class="h-5"
              />
              <span class="text-sm text-gray-600 font-medium">Trade Republic</span>
            </div>
          </div>
          
          <p class="text-gray-600 text-sm leading-relaxed text-balance">
            En quelques clics, vos données seront importées et votre performance calculée automatiquement. <strong>Attention, il s'agit d'une importation ponctuelle</strong> : vos anciennes données restent accessibles, mais elles ne seront pas mises à jour automatiquement. Pour prendre en compte vos derniers achats/ventes, pensez à réimporter vos fichiers régulièrement — nous recommandons une fois par mois.
          </p>
        </div>

      </div>
    </div>
  </ModalContainer>
</template> 